/**
 * Shared utility functions exposed under `theme.utils.*`.
 *
 * Loaded BEFORE main.js and base-element.js. Provides single-source-of-truth
 * implementations for utilities that were previously duplicated across files.
 *
 * Trimmed 2026-05-02: removed throttle, formatMoney, escapeHtml, getCookie/
 * setCookie/deleteCookie, fetchSection, lockScroll/unlockScroll — none had any
 * callers in this theme. Re-add when a caller actually appears.
 * `escapeHtml` was re-added 2026-09-03 under that rule: three sections had each
 * grown a private copy (category-rail, shop-by-category, shop-by-brand).
 *
 * Doc: docs/architecture.md#js-modules
 */

window.theme = window.theme || {};

theme.utils = (() => {
  /**
   * Returns a function that, as long as it continues to be invoked, won't be triggered.
   * @param {Function} fn
   * @param {number} [wait=300]
   * @returns {Function}
   */
  function debounce(fn, wait = 300) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * Escapes the five HTML-significant characters, so untrusted text can never
   * be read as markup by a module that builds HTML strings — a category name
   * from a merchant-supplied JSON file, a brand title, a search term echoed
   * back inside <mark>. Coerces non-strings: callers pass values straight out
   * of parsed JSON, where a name may legitimately arrive as a number.
   *
   * This is the string-concatenation escape hatch only. Prefer `textContent`
   * whenever the value IS the node's whole content — it cannot be forgotten.
   * @param {*} s
   * @returns {string}
   */
  const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
  }

  /**
   * Lazily loads an external script. Caches the promise so each src is fetched once.
   * @param {string} src
   * @returns {Promise<HTMLScriptElement>}
   */
  const scriptCache = new Map();
  function loadScript(src) {
    if (scriptCache.has(src)) return scriptCache.get(src);
    const p = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.defer = true;
      s.onload = () => resolve(s);
      s.onerror = reject;
      document.head.appendChild(s);
    });
    scriptCache.set(src, p);
    return p;
  }

  /**
   * Lazily loads an external stylesheet. Caches the promise per href so
   * concurrent callers share the same fetch, and a second call after
   * resolution is a no-op. Mirrors `loadScript` for vendor or per-component
   * CSS that should not block initial paint — used in tandem with
   * `loadScript` when a feature ships paired JS + CSS (OverlayScrollbars,
   * future per-component bundles).
   *
   * The <link> stays in the head after onload — there is no teardown
   * because stylesheet costs are paid once and a removed <link> would
   * un-apply rules on every existing element using the library's classes.
   * @param {string} href
   * @returns {Promise<HTMLLinkElement>}
   */
  const stylesheetCache = new Map();
  function loadStylesheet(href) {
    if (stylesheetCache.has(href)) return stylesheetCache.get(href);
    const p = new Promise((resolve, reject) => {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      l.onload = () => resolve(l);
      l.onerror = reject;
      document.head.appendChild(l);
    });
    stylesheetCache.set(href, p);
    return p;
  }

  /**
   * Traps focus inside a container. Returns a cleanup function.
   * The container is registered in WeakMap so releaseFocus(container) can also
   * tear it down — this matches the legacy `trapFocus`/`removeTrapFocus` API
   * used by older callers that don't keep the cleanup function.
   *
   * @param {HTMLElement} container
   * @param {HTMLElement} [focusEl] - optional initial focus target
   * @returns {Function} cleanup function
   */
  const _trapHandlers = new WeakMap();
  function trapFocus(container, focusEl) {
    if (_trapHandlers.has(container)) _trapHandlers.get(container)();

    const selector = 'a,button,details,iframe,input,select,textarea,[tabindex]:not([tabindex^="-"])';
    function getFocusable() {
      // `offsetParent !== null` already drops display:none subtrees. The
      // visibility check additionally drops elements hidden via
      // `visibility: hidden` — e.g. closed slide-in nav sub-panels, which stay
      // display:block + off-screen so they can animate. Those elements are not
      // natively focusable, so counting them would corrupt the first/last
      // boundary used for the Tab wrap and let focus escape the trap.
      return Array.from(container.querySelectorAll(selector)).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden');
    }

    function onKey(e) {
      if (e.key !== 'Tab') return;
      const els = getFocusable();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    }

    document.addEventListener('keydown', onKey);
    if (focusEl) focusEl.focus();

    const cleanup = () => {
      document.removeEventListener('keydown', onKey);
      _trapHandlers.delete(container);
    };
    _trapHandlers.set(container, cleanup);
    return cleanup;
  }

  /**
   * Releases a focus trap previously installed by trapFocus(container).
   * Safe to call even if no trap is active. Use the returned cleanup function
   * from trapFocus() when possible — releaseFocus() is provided for callers
   * that don't keep the cleanup reference.
   * @param {HTMLElement} container
   */
  function releaseFocus(container) {
    const cleanup = _trapHandlers.get(container);
    if (cleanup) cleanup();
  }

  return {
    debounce,
    escapeHtml,
    loadScript,
    loadStylesheet,
    trapFocus,
    releaseFocus,
  };
})();
